import OpenAI from 'openai';
import { Message as AppMessage } from './types'; // Import our app's Message type
import { modelHasCapability } from './models'; // Import modelHasCapability

// Define extended interfaces for Xai-specific features
interface XaiChatCompletionMessage extends OpenAI.ChatCompletionMessage {
    reasoning_content?: string;
}

interface XaiChatCompletionUsageDetails {
    reasoning_tokens?: number;
}

interface XaiChatCompletionUsage extends OpenAI.CompletionUsage {
    completion_tokens_details?: XaiChatCompletionUsageDetails;
}

// Define return types for better type safety
interface XaiResponse {
    content: string | null;
    reasoningContent?: string;
    usage?: {
        completionTokens?: number;
        reasoningTokens?: number;
    };
}

// Initialize the Xai client using the environment variable and Xai's base URL
const xai = new OpenAI({
    apiKey: process.env.REACT_APP_XAI_API_KEY,
    baseURL: "https://api.x.ai/v1",
    dangerouslyAllowBrowser: true,
});

/**
 * Converts our app's message format to OpenAI's message format (which Xai uses).
 * 
 * @param messages Array of messages in our app's format
 * @returns An array of formatted messages for Xai API
 */
const processMessagesForXai = (messages: any[]): any[] => {
    return messages.map(msg => {
        let role = msg.role;
        let content = msg.content;

        if (typeof content !== 'string') {
            content = JSON.stringify(content);
        }

        return { role, content };
    });
};

/**
 * Sends a message to the specified Xai model and returns a completion.
 * Now supports streaming responses.
 *
 * @param model The ID of the Xai model to use (e.g., 'grok-3-mini-beta').
 * @param messages An array of message objects representing the conversation history.
 * @param maxTokens The maximum number of tokens to generate.
 * @param stream Whether to use streaming (default: true)
 * @returns A stream of completion chunks or a complete response object.
 */
export const sendXaiMessage = async (
    model: string,
    messages: any[],
    maxTokens: number = 1024,
    stream: boolean = true
): Promise<any> => {
    try {
        const formattedMessages = processMessagesForXai(messages);

        // If streaming is requested, return a stream of responses
        if (stream) {
            return await xai.chat.completions.create({
                model: model,
                messages: formattedMessages,
                max_tokens: maxTokens,
                stream: true
            });
        } else {
            // Non-streaming version (original behavior)
            const completion = await xai.chat.completions.create({
                model: model,
                messages: formattedMessages,
                max_tokens: maxTokens
            });

            // Use type assertions for Xai-specific extensions
            const xaiMessage = completion.choices[0].message as XaiChatCompletionMessage;
            const xaiUsage = completion.usage as XaiChatCompletionUsage;

            return {
                content: xaiMessage.content,
                reasoningContent: xaiMessage.reasoning_content,
                usage: {
                    completionTokens: xaiUsage?.completion_tokens,
                    reasoningTokens: xaiUsage?.completion_tokens_details?.reasoning_tokens
                }
            };
        }
    } catch (error) {
        console.error('Error calling Xai API:', error);
        throw error;
    }
}; 